import { supabase } from '../supabase';
import { sortEnabledTestTypes } from '../utils/testTypeOrdering';

/** Coach-Owned Test Types (058) — the canonical Team availability
 * resolver: FFN tests (always available) UNION custom tests owned by
 * ANY currently-active Head Coach of this team (server-unioned across
 * every active Head Coach when a team has more than one — never
 * arbitrarily picked). This RPC is the source of truth for "what tests
 * are currently available to this Team" — never reconstructed
 * independently here or in any component; this is a thin wrapper only.
 * Sorted via the shared, pure sortEnabledTestTypes() (utils/
 * testTypeOrdering.js) — the resolved rows carry no program_sort_order,
 * so this falls back to test_types' own sort_order/display_name, exactly
 * as that comparator already does when no override is present. */
export async function listTeamAvailableTestTypes(teamId) {
  const { data, error } = await supabase.rpc('list_team_available_test_types', { p_team_id: teamId });
  if (error) throw error;
  return sortEnabledTestTypes(data || []);
}

/** A coach's own test library — active AND archived, so the "My Tests"
 * manager can list (and offer to restore) an archived test, not just
 * currently-available ones. Not a new RPC: test_types already carries a
 * blanket authenticated-read RLS policy, so this is a plain, scoped
 * query — the same posture the retired listProgramCustomTestTypes()
 * used, never a new security boundary. */
export async function listMyCoachTestTypes(profileId) {
  const { data, error } = await supabase
    .from('test_types')
    .select('*')
    .eq('owner_type', 'coach')
    .eq('owner_profile_id', profileId)
    .order('display_name');
  if (error) throw error;
  return data || [];
}

/** Creates a coach-owned custom test type (058's create_coach_test_type()).
 * The server generates the test's key — callers never construct or guess
 * one. No teamId — ownership belongs to the coach/profile, never a Team.
 * Returns the new test_types row. */
export async function createCoachTestType({
  displayName, category, unit, betterDirection, supportsAppStopwatch, recommendedAttempts,
}) {
  const { data, error } = await supabase.rpc('create_coach_test_type', {
    p_display_name: displayName,
    p_category: category,
    p_unit: unit,
    p_better_direction: betterDirection,
    p_supports_app_stopwatch: supportsAppStopwatch,
    p_recommended_attempts: recommendedAttempts || 3,
  });
  if (error) throw error;
  return data;
}

/** Edits a coach's own custom test type — every field is optional (omit
 * whatever isn't changing). unit/betterDirection/supportsAppStopwatch are
 * rejected server-side once any attempt exists for this test (058's own
 * historical-safety rule, carried forward from 056). `active: false`
 * archives the test (removes it from new-capture availability without
 * touching any historical attempt, PB, leaderboard fact, or correction/
 * invalidation eligibility); `active: true` restores it. Returns the
 * updated test_types row. */
export async function updateCoachTestType({
  testTypeKey, displayName, category, recommendedAttempts, unit, betterDirection, supportsAppStopwatch, active,
}) {
  const { data, error } = await supabase.rpc('update_coach_test_type', {
    p_test_type_key: testTypeKey,
    p_display_name: displayName ?? null,
    p_category: category ?? null,
    p_recommended_attempts: recommendedAttempts ?? null,
    p_unit: unit ?? null,
    p_better_direction: betterDirection ?? null,
    p_supports_app_stopwatch: supportsAppStopwatch ?? null,
    p_active: active ?? null,
  });
  if (error) throw error;
  return data;
}
